import {notNull} from '../../../../../../library/clxProductNet/assets/js/modules/common/assert';
import {debounce} from '../../common/debounce';

export default class RecombeeSearch {
    private readonly MOBILE_BREAKPOINT = 639;
    private readonly FOCUS_BREAKPOINT = 539;

    private rootElement: HTMLElement;
    private recombeeInput: HTMLInputElement;
    private searchResultContainer: HTMLDivElement;
    private readonly searchUrl: string;
    private focusCallback?: () => void;

    private inputContainer: HTMLDivElement;
    private searchIcon: HTMLButtonElement;
    private body: HTMLBodyElement;
    private closeOverlayButton: HTMLDivElement | null;

    constructor() {
        this.rootElement = notNull(document.querySelector('.h_search.recombee')) as HTMLElement;
        this.inputContainer = notNull(this.rootElement.querySelector('.input-container')) as HTMLDivElement;
        this.recombeeInput = notNull(this.inputContainer.querySelector('#recombee_input')) as HTMLInputElement;
        this.searchIcon = notNull(this.rootElement.querySelector('.search-toggle')) as HTMLButtonElement;
        this.searchResultContainer = notNull(this.rootElement.querySelector('#recombee_search_result')) as HTMLDivElement;
        this.body = notNull(document.querySelector('body')) as HTMLBodyElement;

        const searchUrlElement = notNull(document.querySelector('ul#ajax-urls li#ajax-autocomplete-fulltextsearch'));
        this.searchUrl = searchUrlElement.textContent!.trim();

        this.closeOverlayButton = this.inputContainer.querySelector('.close-overlay') as HTMLDivElement | null;

        this._attachSearchListener();
        this._attachFocusListener();
        this._attachMobileSearchToggleListener();
        this._attachGlobalCloseListeners();
        this._attachCloseOverlayListener();
        this._attachCustomTabHandling();
        this._updateButtonFocusability();

        window.addEventListener('resize', () => {
            this._updateButtonFocusability();
        });
    }

    public setFocusCallback(callback: () => void): void {
        this.focusCallback = callback;
    }

    public closeResult(): void {
        const overlay = document.querySelector('.overlay');
        if (overlay) {
            overlay.remove();
        }

        this.body.removeAttribute('style');
        this.recombeeInput.value = '';
        this.searchResultContainer.innerHTML = '';
        this.recombeeInput.setAttribute('aria-expanded', 'false');
        this.searchResultContainer.removeAttribute('role');
        this.searchResultContainer.removeAttribute('aria-label');

        const orderFormHint = document.getElementById('orderform_hint') as HTMLDivElement | null;
        if (orderFormHint) {
            orderFormHint.style.display = 'block';
        }

        this.inputContainer.classList.remove('visible');
    }

    private _attachSearchListener() {
        this.recombeeInput.addEventListener(
            'keyup',
            debounce(() => {
                this.recombeeSearch();
            }, 500),
        );
    }

    private _attachFocusListener() {
        this.recombeeInput.addEventListener('focus', () => {
            if (document.activeElement === this.recombeeInput) {
                this.focusCallback?.();
            }
        });
    }

    private _attachMobileSearchToggleListener() {
        this.searchIcon.addEventListener('click', (event) => {
            if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
                event.preventDefault();
                event.stopPropagation();

                const isVisible = this.inputContainer.classList.toggle('visible');
                if (isVisible) {
                    this.recombeeInput.focus();
                } else {
                    this.closeResult();
                }
            }
        });
    }

    private _attachGlobalCloseListeners() {
        window.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                if (this.inputContainer.classList.contains('visible')) {
                    this.closeResult();
                    this.recombeeInput.focus();
                }
            }
        });

        // Close if user clicks outside the search component
        document.addEventListener('click', (event: MouseEvent) => {
            if (!this.rootElement.contains(event.target as Node)) {
                this.closeResult();
            }
        });

        // Close if focus moves outside the search component (e.g. via Tab)
        document.addEventListener('focusin', (event: FocusEvent) => {
            const target = event.target as HTMLElement;
            if (!this.rootElement.contains(target)) {
                this.closeResult();
            }
        });

        // Close if input loses focus and there are no search results
        this.recombeeInput.addEventListener('blur', () => {
            setTimeout(() => {
                const hasResults = this.searchResultContainer.innerHTML.trim().length > 0;
                if (!hasResults) {
                    this.closeResult();
                }
            }, 100);
        });

        // Close if focus returns to search icon
        this.searchIcon.addEventListener('focusin', () => {
            if (this.inputContainer.classList.contains('visible')) {
                this.closeResult();
            }
        });
    }

    private _attachCloseOverlayListener() {
        if (this.closeOverlayButton) {
            this.closeOverlayButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.closeResult();
            });
        }
    }

    private _attachCustomTabHandling() {
        this.recombeeInput.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Tab' && !event.shiftKey) {
                const firstResult = this.searchResultContainer.querySelector('.item a') as HTMLElement;
                if (firstResult) {
                    event.preventDefault();
                    firstResult.focus();
                }
            }
        });
    }

    private _updateButtonFocusability() {
        if (window.innerWidth <= this.FOCUS_BREAKPOINT) {
            this.searchIcon.removeAttribute('tabindex');
        } else {
            this.searchIcon.setAttribute('tabindex', '-1');
        }
    }

    async recombeeSearch() {
        if (this.recombeeInput.value.length > 2) {
            // @ts-ignore
            if (window.ga) {
                // @ts-ignore
                ga('send', 'pageview', '/suche?q=' + this.recombeeInput.value);
            }

            const request = await fetch(this.searchUrl + 'searchterm/' + encodeURIComponent(this.recombeeInput.value), {
                method: 'GET',
                headers: {'X-Requested-With': 'XMLHttpRequest'},
            });

            if (!request.ok) return;

            this._showResult();
            this.searchResultContainer.innerHTML = await request.text();
            this.searchResultContainer.setAttribute('role', 'listbox');
            this.searchResultContainer.setAttribute('aria-label', 'Suchergebnisse');
            this.recombeeInput.setAttribute('aria-expanded', 'true');
            this.inputContainer.classList.add('visible');
            this._observeLastResultBlur();
        }
    }

    private _showResult() {
        let overBox = document.querySelector('.overlay') as HTMLDivElement | null;

        if (!overBox) {
            overBox = document.createElement('div');
            overBox.classList.add('overlay');
            this.body.appendChild(overBox);
            this.body.style.overflow = 'hidden';

            overBox.addEventListener('click', () => {
                this.closeResult();
            });
        }

        const orderFormHint = document.getElementById('orderform_hint') as HTMLDivElement | null;
        if (orderFormHint) {
            this.searchResultContainer.removeAttribute('style');
            orderFormHint.style.display = 'none';

            const closeButton = document.querySelector('#recombee_search a.nav-close');
            closeButton?.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.closeResult();
            });
        }
    }

    private _observeLastResultBlur() {
        const lastItem = this.searchResultContainer.querySelector('.item:last-of-type a') as HTMLElement;
        if (lastItem) {
            lastItem.addEventListener('blur', () => {
                setTimeout(() => {
                    const active = document.activeElement;
                    const stillInside = this.rootElement.contains(active);
                    if (!stillInside) {
                        this.closeResult();
                    }
                }, 50);
            });
        }
    }
}
